/**
 * src/hooks/useCustomerProfile.ts — Customer profile fetcher + save hook.
 *
 * Reads and writes the authenticated user's Customer row via atticApi →
 * Harper's /Me Resource. Guards against concurrent saves via a synchronous
 * ref set before the async call, so rapid double-taps don't fire twice.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { Customer } from "../types";
import { getMyProfile } from "../api/atticApi";
import { enqueueMutation } from "../api/mutationQueue";
import type { UpdateCustomerProfilePayload } from "../api/customerApi";
import { useAuth } from "../context/AuthContext";
import { useRoleVersion } from "../context/RoleVersionContext";

export interface UseCustomerProfileResult {
  /** The fetched Customer record, or null while loading / on error. */
  profile: Customer | null;
  /** True while the initial Customer fetch is in-flight. */
  loading: boolean;
  /** Non-null when the initial fetch fails. */
  error: string | null;
  /** Re-triggers the initial fetch. */
  refetch: () => void;
  /** True while the profile save mutation is in-flight (AC-004). */
  saveLoading: boolean;
  /** Non-null when the last save attempt returned an error (AC-005). */
  saveError: string | null;
  /**
   * Persist updated profile fields to the Operations API (AC-003).
   * Returns true on success, false on failure (saveError is populated).
   * Clears saveError before each attempt.
   * AC-003 guard: concurrent calls while in-flight are no-ops (returns false).
   */
  save: (payload: UpdateCustomerProfilePayload) => Promise<boolean>;
}

export function useCustomerProfile(): UseCustomerProfileResult {
  const { customerEmail } = useAuth();
  // AC-005 (WP-032): subscribe to role-change version so all instances refetch
  // when DevRoleSwitcher commits a new role to HarperDB.
  const { roleVersion } = useRoleVersion();
  const [profile, setProfile] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const unmountedRef = useRef(false);
  // AC-003: ref-based dedup guard set synchronously before the async API call
  // so concurrent in-flight save() calls are no-ops even before React re-renders
  // with saveLoading=true (state setters are async; refs are synchronous).
  const saveInFlightRef = useRef(false);

  const fetchProfile = useCallback(async () => {
    if (!customerEmail) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await getMyProfile();

      if (unmountedRef.current) return;

      if (!res.data) {
        setError("Customer record not found. Please contact support.");
        setProfile(null);
      } else {
        setProfile(res.data as unknown as Customer);
      }
    } catch (err) {
      if (unmountedRef.current) return;
      const message = err instanceof Error ? err.message : "Unknown error";
      setError("Could not load your profile: " + message);
    } finally {
      if (!unmountedRef.current) setLoading(false);
    }
  // roleVersion is intentionally included: when it increments, this callback
  // gets a new reference, causing the useEffect below to fire and re-fetch.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerEmail, roleVersion]);

  const save = useCallback(
    async (payload: UpdateCustomerProfilePayload): Promise<boolean> => {
      if (!profile?.id) {
        setSaveError("Profile not loaded — cannot save.");
        return false;
      }

      // AC-003: synchronous dedup guard prevents a second API call if save() is
      // invoked again before the component re-renders with saveLoading=true.
      if (saveInFlightRef.current) return false;
      saveInFlightRef.current = true;

      setSaveLoading(true);
      setSaveError(null);

      try {
        // Delivery-info defaults are sent as explicit empty strings when
        // cleared, not omitted — that lets the user remove a gate code
        // they no longer need (e.g., moved apartments). Only omit when
        // the key isn't in the payload at all.
        const result = await enqueueMutation<{ success: boolean }>({
          type: 'update_profile',
          payload: {
            fields: {
              name: payload.name.trim(),
              address: payload.address.trim(),
              phone: payload.phone.trim(),
              ...(payload.default_pickup_method ? { default_pickup_method: payload.default_pickup_method } : {}),
              ...(payload.address_components ? { address_components: payload.address_components } : {}),
              ...(payload.gate_code !== undefined ? { gate_code: payload.gate_code.trim() } : {}),
              ...(payload.buzzer_apartment !== undefined ? { buzzer_apartment: payload.buzzer_apartment.trim() } : {}),
              ...(payload.special_instructions !== undefined ? { special_instructions: payload.special_instructions.trim() } : {}),
              ...(payload.location_type !== undefined ? { location_type: payload.location_type } : {}),
              ...(payload.floor_number !== undefined ? { floor_number: payload.floor_number } : {}),
            },
          },
        });

        if (unmountedRef.current) return false;

        if (result.success) {
          // Optimistically update local profile state so dashboard prompt recalculates.
          setProfile((prev) =>
            prev
              ? {
                  ...prev,
                  name: payload.name.trim(),
                  address: payload.address.trim(),
                  phone: payload.phone.trim(),
                  ...(payload.default_pickup_method ? { default_pickup_method: payload.default_pickup_method } : {}),
                  ...(payload.gate_code !== undefined ? { gate_code: payload.gate_code.trim() } : {}),
                  ...(payload.buzzer_apartment !== undefined ? { buzzer_apartment: payload.buzzer_apartment.trim() } : {}),
                  ...(payload.special_instructions !== undefined ? { special_instructions: payload.special_instructions.trim() } : {}),
                  ...(payload.location_type !== undefined ? { location_type: payload.location_type } : {}),
                  ...(payload.floor_number !== undefined ? { floor_number: payload.floor_number } : {}),
                  updated_at: new Date().toISOString(),
                }
              : prev,
          );
          return true;
        } else {
          setSaveError("Failed to save profile");
          return false;
        }
      } finally {
        // Always reset the guard and loading flag so the button re-enables
        // regardless of success, error, or unexpected exception.
        saveInFlightRef.current = false;
        if (!unmountedRef.current) setSaveLoading(false);
      }
    },
    [profile],
  );

  useEffect(() => {
    unmountedRef.current = false;
    void fetchProfile();
    return () => {
      unmountedRef.current = true;
    };
  }, [fetchProfile]);

  return { profile, loading, error, refetch: fetchProfile, saveLoading, saveError, save };
}
